import { logger } from "@justhome/common";
import { createContext, useContext } from "react";
import de from "../../../locales/de.json";
import en from "../../../locales/en.json";
import immo from "../../../locales/immo.json";
import { DEFAULT_LOCALE } from "../constants";
import { Locale } from "../types";

const dictionaries: Record<string, Record<string, string>> = {
  en,
  de,
  immo,
};

export const getKey = (locale: string, key: string) => {
  if (!dictionaries[locale]?.[key] && !dictionaries[DEFAULT_LOCALE]?.[key]) {
    logger.warn(`Translation key not found: ${key}`);
    return key;
  }

  return dictionaries[locale]?.[key] || dictionaries[DEFAULT_LOCALE]?.[key];
};

export type TFunction = (
  key: string,
  options?: Record<string, string>,
) => string;

export interface TranslationContextValue {
  locale: Locale;
  t: TFunction;
}

const defaultValue: TranslationContextValue = {
  locale: DEFAULT_LOCALE,
  t: (key: string) => key,
};

export const TranslationsContext =
  createContext<TranslationContextValue>(defaultValue);

TranslationsContext.displayName = "Translations";

export const useTranslation = () => {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error(
      "useTranslation must be used within a TranslationsProvider",
    );
  }
  return context;
};

interface TranslationsProviderProps {
  locale: "en" | "de" | "immo";
  children: React.ReactNode;
}

export function TranslationsProvider({
  children,
  locale,
}: TranslationsProviderProps) {
  const t: TFunction = (key, options = {}) => {
    const translation = getKey(locale, key) ?? key;

    return Object.keys(options).reduce((str, key) => {
      const value = options[key];
      const regex = new RegExp(`{{${key}}}`, "g");
      return (str ?? "").replace(regex, value ?? "");
    }, translation);
  };

  return (
    <TranslationsContext.Provider value={{ locale, t }}>
      {children}
    </TranslationsContext.Provider>
  );
}
